export interface BaseFileSystemItem {
  name: string;
  path: string;
  driveId?: string;
  id?: string;
  parentId?: string;
  webUrl?: string;
  size?: number;
  lastModifiedDateTime?: string;
  lastModifiedBy?: string; // resolved display name, not the raw identitySet
}

export interface DirectoryItem extends BaseFileSystemItem {
  type: 'directory';
  hasFolderChildren: boolean;
  childCount?: number;
}

export interface FileItem extends BaseFileSystemItem {
  type: 'file';
}

// A backing store grafted into the path namespace at a prefix (one of a project's provisioned
// document libraries). Not a driveItem: it cannot be moved, renamed or deleted through this API (that
// needs ManageLists), so it is a distinct union member rather than a DirectoryItem with a flag — every
// `item.type === 'directory'` call site that must defend against one gets a compiler-checked reminder
// instead of a re-derived path check.
//
// Carries no mount identity of its own: the row's `path` IS the mount's prefix, so any call site that
// needs the mount asks `mountAt(item.path)` rather than trusting a copied key.
export interface MountItem extends BaseFileSystemItem {
  type: 'mount';
  hasFolderChildren: boolean;
}

/**
 * An intermediate level of the path namespace that no mount sits at — the project rows a
 * multi-project mount table implies, where `/1234 - Foo` exists purely because `/1234 - Foo/CESG`
 * and `/1234 - Foo/Delad` do.
 *
 * Its own union member rather than a `MountItem` with no drive: a MountItem for which `mountAt()`
 * returns undefined would be a trap, since every mutation reaches for `resolve(item.path)` (D2). It
 * carries nothing beyond a name, a path and a chevron — no driveId, no id, no size, because there is
 * no driveItem behind it. `hasFolderChildren` is a literal `true`: a namespace only exists because
 * something is mounted below it.
 */
export interface NamespaceItem extends BaseFileSystemItem {
  type: 'namespace';
  hasFolderChildren: true;
}

export type FileSystemItem =
  | DirectoryItem
  | FileItem
  | MountItem
  | NamespaceItem;

/**
 * Not a driveItem: there is no PATCH, copy, rename or delete that acts on one, at any permission
 * tier (renaming or deleting a document library needs ManageLists; a namespace has no backing object
 * at all). The predicate every such guard should narrow with, rather than re-deriving
 * `type !== 'mount'` and quietly missing the next synthetic variant.
 */
export type SyntheticItem = MountItem | NamespaceItem;

export const isSynthetic = (item: FileSystemItem): item is SyntheticItem =>
  item.type === 'mount' || item.type === 'namespace';

// Renders and navigates like a folder (chevron, DropZone target, folder icon, dbl-click navigate) —
// the union every "is this a folder-shaped row" call site should narrow to, instead of re-deriving
// `type === 'directory'` by hand each time a new folder-shaped variant is added.
export type FolderLikeItem = DirectoryItem | SyntheticItem;

export const isFolderLike = (item: FileSystemItem): item is FolderLikeItem =>
  item.type === 'directory' || isSynthetic(item);
